import { useCallback, useEffect, useMemo, useState } from 'react';

import { ChevronDownIcon, PlusIcon, SettingsGearIcon, UpgradeChipIcon } from '@braskai/icons';
import { Chip, Icon, Menu, SpeakersPaletteTokens, Typography, useDeviceType } from '@braskai/ui-kit';
import { useNavigate, useParams } from 'react-router-dom';

import { Stack } from '@mui/material';

import { useIsOwnPaidTrial } from '@/features/pricing/paidTrial';
import {
  useCanCreateSpace,
  useCreateTeamspaceModal,
  useTeamspaceSettingsModal,
  useTeamspaceWelcomeModal,
} from '@/features/teamspace';
import { OnboardingTooltip, useOnboardingEvent } from '@/features/user/onboarding';

import { useBalanceStore, useUserBalanceStore } from '@/entities/balance';
import { useUpgradePlanModalStore } from '@/entities/pricing';
import {
  TeamspaceBanner,
  TeamspaceMenu,
  TeamspaceMenuItem,
  useGetSpaceById,
  useGetSpaces,
} from '@/entities/teamspace';
import { useCurrentUser } from '@/entities/user/currentUser';

import { IS_WHITE_LABEL } from '@/shared/environments';
import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PlanCategory } from '@/shared/types/Billing';
import { NavigationItem } from '@/shared/ui/NavigationItem';

import {
  SWorkspaceAvatar,
  SWorkspaceButton,
  SWorkspaceChevron,
  SWorkspaceName,
  SWorkspacePlan,
  SWorkspaceText,
} from './styled';

const MENU_WIDTH = 231;

type TeamspacesNavProps = {
  opened?: boolean;
  handleCloseSidebar?: () => void;
};

export const TeamspacesNav = ({ opened, handleCloseSidebar }: TeamspacesNavProps) => {
  const { t } = useInterfaceLanguage();
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { data: spaces } = useGetSpaces();
  const { data: currentSpace } = useGetSpaceById(spaceId || '');
  const { data: userId } = useCurrentUser((data) => data.id);
  const { isMobile } = useDeviceType();
  const {
    hasActiveSubscription,
    hasFreeSubscription,
    hasTrialPlan,
    isBalanceLoaded,
    planCategory,
    planName: userPlanName,
  } = useUserBalanceStore();
  const { isNewUser, showTooltip } = useTeamspaceWelcomeModal();
  const setOnboardingDiscoverTeamspaceEvent = useOnboardingEvent('discoverTeamspace_v1', true);
  const { hasActiveEnterprisePlan } = useBalanceStore();
  const isPaidTrial = useIsOwnPaidTrial();

  const spaceName = currentSpace?.name || t('sidebar.teamspace.defaultName');
  const isOwner = currentSpace?.owner_id === userId;
  const isFreePlan = isBalanceLoaded && (hasTrialPlan || hasFreeSubscription);
  const paidPlanLabel = isPaidTrial ? t('paidTrial.planLabel') : userPlanName;
  const freePlanLabel = isFreePlan ? t('sidebarNav.plan.free') : paidPlanLabel;
  const planName = IS_WHITE_LABEL ? undefined : freePlanLabel;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const canCreateSpace = useCanCreateSpace();

  const showTeamspaceBanner =
    !hasActiveSubscription ||
    hasTrialPlan ||
    planCategory === PlanCategory.Starter ||
    (planCategory === PlanCategory.Creator && !isPaidTrial);

  const openCreateTeamspaceModal = useCreateTeamspaceModal((state) => state.open);
  const openUpgradePlanModal = useUpgradePlanModalStore((state) => state.open);
  const openTeamspaceSettingsModal = useTeamspaceSettingsModal((state) => state.open);

  const handleUpgrade = useCallback(() => {
    openUpgradePlanModal({
      limitedFeature: 'teamspace',
      plan: hasActiveEnterprisePlan ? 'custom' : 'enterprise',
      pricingEntryPoint: 'sidebar_teamspace_limit',
    });
    handleCloseSidebar?.();
    MixpanelEvents.addMoreSpacesUpgradeClick();
  }, [handleCloseSidebar, hasActiveEnterprisePlan, openUpgradePlanModal]);

  const handleFreeUserUpgrade = useCallback(() => {
    openUpgradePlanModal({ limitedFeature: 'teamspace', pricingEntryPoint: 'sidebar_create_teamspace' });
    handleCloseSidebar?.();
    MixpanelEvents.teamSpaceUpgradeClick();
  }, [handleCloseSidebar, openUpgradePlanModal]);

  const handleSettingsClick = useCallback(() => {
    openTeamspaceSettingsModal();
    handleCloseSidebar?.();
  }, [handleCloseSidebar, openTeamspaceSettingsModal]);

  const handleCreateTeamspaceClick = useCallback(() => {
    openCreateTeamspaceModal();
    handleCloseSidebar?.();
    MixpanelEvents.createNewSpace();
  }, [handleCloseSidebar, openCreateTeamspaceModal]);

  const spaceOptions = useMemo(() => {
    const options = [
      ...(spaces?.map((space) => ({
        id: space.id,
        label: (
          <TeamspaceMenuItem
            name={space.name}
            color={(space.color as SpeakersPaletteTokens) || 'midnight'}
            isActive={space.id === spaceId}
            isMobile={isMobile}
            isOwner={space.owner_id === userId}
            membersCount={space.members.length + (space.invites?.length ?? 0)}
          />
        ),
        action: () => {
          navigate(`/spaces/${space.id}`);
        },
        disabled: !space.is_active && space.owner_id !== userId,
        isActive: space.id === spaceId,
      })) || [
        {
          id: 'mock_space',
          label: (
            <TeamspaceMenuItem
              name={t('sidebar.teamspace.defaultName')}
              color="midnight"
              membersCount={1}
              isActive
              isOwner
            />
          ),
          disabled: false,
          isActive: true,
        },
      ]),
    ];

    if (!showTeamspaceBanner) {
      options.push({
        id: 'new-space',
        label: (
          <Stack
            alignItems="center"
            data-testid="new-teamspace-btn"
            direction="row"
            gap={4}
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" gap={1}>
              <Icon color="grey.500" component={<PlusIcon />} size={16} />
              <Typography color="grey.900" variant="caption-1-md">
                {t('sidebar.teamspace.new')}
              </Typography>
            </Stack>

            {!canCreateSpace && (
              <UpgradeChipIcon
                style={{
                  borderRadius: 4,
                  height: 20,
                  width: 20,
                }}
              />
            )}
          </Stack>
        ),
        action: canCreateSpace ? handleCreateTeamspaceClick : handleUpgrade,

        disabled: false,
        isActive: false,
      });
    }

    return options;
  }, [
    spaces,
    showTeamspaceBanner,
    userId,
    spaceId,
    isMobile,
    navigate,
    handleCreateTeamspaceClick,
    handleUpgrade,
    canCreateSpace,
    t,
  ]);

  useEffect(() => {
    if (!opened) {
      setIsMenuOpen(false);
    }
  }, [opened]);

  useEffect(() => {
    if (!isNewUser) {
      setOnboardingDiscoverTeamspaceEvent();
    }
  }, [isNewUser, setOnboardingDiscoverTeamspaceEvent]);

  if (isMobile) {
    return (
      <Stack direction="row" gap={8} height={44}>
        <TeamspaceMenu name={spaceName} isOwner={isOwner} options={spaceOptions} sx={{ minWidth: 0 }} />
        <NavigationItem
          sx={{
            flexShrink: 0,
            marginBottom: 0,
            marginTop: 0,
            width: 44,
          }}
          component="div"
          data-testid="teamspaces-nav-settings-click"
          icon={<SettingsGearIcon height={20} width={20} />}
          isMobile
          isSquare
          onClick={handleSettingsClick}
        />
      </Stack>
    );
  }

  return (
    <OnboardingTooltip
      open={isNewUser ? showTooltip : true}
      stepName={isNewUser ? 'manageTeamspace_v1' : 'discoverTeamspace_v1'}
    >
      <div>
        <Menu
          bottomBanner={
            showTeamspaceBanner && (
              <TeamspaceBanner
                data-testid="teamspaces-nav-free-user-upgrade"
                dataTestId="free-plan-teamspace-banner"
                description={t('sidebar.teamspace.description')}
                isImage
                onClick={handleFreeUserUpgrade}
              >
                <Chip label={t('common.upgrade')} color="primary" isColoredBg={false} size="md" variant="squared" />
              </TeamspaceBanner>
            )
          }
          open={isMenuOpen}
          options={spaceOptions}
          placement="bottom"
          sx={{ [`& > div:first-of-type`]: { width: '100%' }, margin: '0 8px 8px' }}
          width={MENU_WIDTH}
          disableMobileView
          handleOpen={setIsMenuOpen}
        >
          <SWorkspaceButton
            $isOpen={Boolean(opened)}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            data-testid="teamspace-menu"
          >
            <SWorkspaceAvatar aria-hidden>{spaceName.trim().charAt(0).toUpperCase()}</SWorkspaceAvatar>
            <SWorkspaceText>
              <SWorkspaceName title={spaceName}>{spaceName}</SWorkspaceName>
              {planName && <SWorkspacePlan title={planName}>{planName}</SWorkspacePlan>}
            </SWorkspaceText>
            <SWorkspaceChevron $isMenuOpen={isMenuOpen} aria-hidden>
              <ChevronDownIcon />
            </SWorkspaceChevron>
          </SWorkspaceButton>
        </Menu>
      </div>
    </OnboardingTooltip>
  );
};
